import { useId, useState, type ReactNode } from 'react';
import { Check, Globe, Lock } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import type { PresenceChat } from '../../../lib/presence';
import type { PresenceAction, PresenceCard, PresenceChecklistRow } from '../../../lib/presence-checklist';
import type { I18nCopy } from '../../../lib/space-resources';
import type { WorkspaceWebsite } from '../../../lib/workspace-website';
import { Button, Card, ProgressBar, TextButton, Toast, Typography, type ButtonProps } from '../ui';
import styles from './PresenceChecklist.module.css';

// The Online presence card's go-professional checklist (design "B") and the action buttons it shares with the Space
// header strip (design "H3"). Every decision — which steps, their words, which button, where it goes — is
// `lib/presence-checklist.ts` (unit-tested, mirrored by both phones); this file only draws it.

/** Resolves a model copy (a catalog key, or display text as-is), with `{productName}` always available. */
export function usePresenceCopy() {
  const { t } = useTranslation();
  const productName = useProductName();
  return (copy: I18nCopy | { text: string }) => ('text' in copy ? copy.text : t(copy.key, { productName, ...copy.values }));
}

/**
 * The chat a checklist/header action opens: about the site when there is one, else about the Space (a Space with no
 * website yet).
 */
export function presenceActionChat(message: I18nCopy, website: WorkspaceWebsite | null): PresenceChat {
  return website ? { message, context: { kind: 'website', website } } : { message, context: { kind: 'space' } };
}

/**
 * A `desktopOnly` action's tap: the notice it shows ("Editing is available only on desktop."), as a toast, plus the
 * props that make its button LOOK disabled yet stay tappable — `aria-disabled` (the DS paints its disabled skin and
 * screen readers announce it unavailable) and a description carrying the notice. Never a real `disabled`: the reader
 * has to be able to tap it to learn why.
 */
function useDesktopOnlyNotice(action: PresenceAction) {
  const copy = usePresenceCopy();
  const id = useId();
  const [open, setOpen] = useState(false);
  if (action.target.kind !== 'desktopOnly') return null;
  const message = copy(action.target.message);
  return {
    props: { 'aria-disabled': true as const, 'aria-describedby': id },
    show: () => setOpen(true),
    extras: (
      <>
        <span id={id} hidden>
          {message}
        </span>
        <Toast open={open} message={message} severity="info" onClose={() => setOpen(false)} />
      </>
    ),
  };
}

/**
 * One presence action as a DS button: a url opens in a new tab, a chat opens the Space's chat, and a desktop-only
 * editor on a phone looks disabled and shows its notice. `onAct` runs first on all of them (the caller's BI, and its
 * chat handoff for a chat target).
 */
export function PresenceActionButton({
  action,
  onAct,
  variant = 'contained',
  color = 'standard',
  size = 'small',
  className,
  testId,
}: {
  action: PresenceAction;
  onAct: (action: PresenceAction) => void;
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
  size?: ButtonProps['size'];
  className?: string;
  testId?: string;
}) {
  const copy = usePresenceCopy();
  const label = copy(action.label);
  const notice = useDesktopOnlyNotice(action);
  if (notice) {
    return (
      <>
        <Button
          variant={variant}
          color={color}
          size={size}
          className={className}
          data-testid={testId}
          {...notice.props}
          onClick={() => {
            onAct(action);
            notice.show();
          }}
        >
          {label}
        </Button>
        {notice.extras}
      </>
    );
  }
  if (action.target.kind === 'url') {
    return (
      <Button
        href={action.target.url}
        external
        target="_blank"
        rel="noopener noreferrer"
        variant={variant}
        color={color}
        size={size}
        className={className}
        data-testid={testId}
        onClick={() => onAct(action)}
      >
        {label}
      </Button>
    );
  }
  return (
    <Button variant={variant} color={color} size={size} className={className} data-testid={testId} onClick={() => onAct(action)}>
      {label}
    </Button>
  );
}

/** A quiet text link for the card's Edit line (Edit · Dashboard) and the site strip's cells. */
export function PresenceTextAction({
  action,
  onAct,
  testId,
  endIcon,
}: {
  action: PresenceAction;
  onAct: (action: PresenceAction) => void;
  testId?: string;
  /** A trailing glyph (the site strip's View site ↗). */
  endIcon?: ReactNode;
}) {
  const copy = usePresenceCopy();
  const label = copy(action.label);
  const notice = useDesktopOnlyNotice(action);
  if (notice) {
    return (
      <>
        <TextButton
          size="small"
          endIcon={endIcon}
          className={styles.link}
          data-testid={testId ?? `presenceChecklistLink-${action.bi}`}
          {...notice.props}
          onClick={() => {
            onAct(action);
            notice.show();
          }}
        >
          {label}
        </TextButton>
        {notice.extras}
      </>
    );
  }
  return action.target.kind === 'url' ? (
    <TextButton
      href={action.target.url}
      external
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      endIcon={endIcon}
      className={styles.link}
      data-testid={testId ?? `presenceChecklistLink-${action.bi}`}
      onClick={() => onAct(action)}
    >
      {label}
    </TextButton>
  ) : (
    <TextButton size="small" endIcon={endIcon} className={styles.link} data-testid={testId ?? `presenceChecklistLink-${action.bi}`} onClick={() => onAct(action)}>
      {label}
    </TextButton>
  );
}

const CIRCLE_CLASS: Record<PresenceChecklistRow['state'], string> = {
  DONE: styles.circleDone,
  NEXT: styles.circleNext,
  TODO: styles.circleTodo,
  LOCKED: styles.circleLocked,
};

function StepCircle({ state }: { state: PresenceChecklistRow['state'] }) {
  return (
    <span className={`${styles.circle} ${CIRCLE_CLASS[state]}`} aria-hidden>
      {state === 'DONE' && <Check size={12} strokeWidth={3} />}
      {state === 'LOCKED' && <Lock size={10} />}
    </span>
  );
}

/** The checklist card: thumbnail + title + "{done} of {total} done" + progress, one row per step, then Edit · Dashboard. */
export function PresenceChecklistCard({
  card,
  onAct,
  menu,
}: {
  card: PresenceCard;
  onAct: (action: PresenceAction) => void;
  /** The main website row's ⋯ (Open · Make main · Move · Remove), when it has one. */
  menu?: ReactNode;
}) {
  const { t } = useTranslation();
  const copy = usePresenceCopy();
  const percent = card.total > 0 ? (card.done / card.total) * 100 : 0;
  const complete = card.total > 0 && card.done === card.total;
  return (
    <Card variant="outline" size="sm" className={styles.card} data-testid="presenceChecklist">
      <div className={styles.head}>
        <span className={styles.thumb}>
          {card.thumbnailUrl ? <img src={card.thumbnailUrl} alt="" className={styles.thumbImg} loading="lazy" /> : <Globe size={18} aria-hidden />}
        </span>
        <span className={styles.headText}>
          <Typography variant="p2" color="strong" component="span" className={styles.title}>
            {copy(card.title)}
          </Typography>
          {card.total > 0 && (
            <>
              <Typography variant="p3" color="secondary" component="span">
                {t('presence.checklist.progress', { done: String(card.done), total: String(card.total) })}
              </Typography>
              <ProgressBar progress={complete ? 'success' : 'neutral'} value={percent} decorative className={styles.progress} />
            </>
          )}
        </span>
        {menu}
      </div>
      {card.rows.length > 0 && (
        <ol className={styles.steps}>
          {card.rows.map((row, i) => (
            <li key={`${row.kind}-${i}`} className={styles.step} data-testid={`presenceStep-${row.kind}`} data-state={row.state}>
              <StepCircle state={row.state} />
              <span className={styles.stepText}>
                <Typography variant="p2" color={row.state === 'LOCKED' ? 'secondary' : 'primary'} component="span">
                  {copy(row.title)}
                </Typography>
                {row.detail && (
                  <Typography variant="p3" color="secondary" component="span">
                    {copy(row.detail)}
                  </Typography>
                )}
                {row.action && row.primary && (
                  <span className={styles.stepPrimary}>
                    <PresenceActionButton action={row.action} onAct={onAct} color="accent" testId={`presenceStepAction-${row.kind}`} />
                  </span>
                )}
              </span>
              {row.action && !row.primary && (
                <PresenceActionButton action={row.action} onAct={onAct} variant="outlined" size="tiny" className={styles.stepSecondary} testId={`presenceStepAction-${row.kind}`} />
              )}
            </li>
          ))}
        </ol>
      )}
      {(card.edit || card.review || card.dashboard) && (
        <div className={styles.editLine} data-testid="presenceChecklistEdit">
          {card.edit && <PresenceTextAction action={card.edit} onAct={onAct} />}
          {card.edit && card.review && (
            <span className={styles.separator} aria-hidden>
              ·
            </span>
          )}
          {card.review && <PresenceTextAction action={card.review} onAct={onAct} />}
          {(card.edit || card.review) && card.dashboard && (
            <span className={styles.separator} aria-hidden>
              ·
            </span>
          )}
          {card.dashboard && <PresenceTextAction action={card.dashboard} onAct={onAct} />}
        </div>
      )}
    </Card>
  );
}
